import { fetchProjectCompanies } from '@/api/v4/projects.api';
import { CommentsTab } from '@/features/action-panel/comments/comments-tab';
import { modalState } from '@/state/modal.state';
import type { CustomAsyncSelectProps } from '@/ui/select/async/use-select-type';

import { projectIdState } from '../../project.state';

import { useProjectComments } from './use-project-comments.query';
import { useProjectCompanies } from '../../use-project-companies.query';
import { useCreateProjectComment } from './use-create-project-comment.mutation';
import { useUpdateProjectComment } from './use-update-project-comment.mutation';
import { useDeleteProjectComment } from './use-delete-project-comment.mutation';
import { useAtomValue, useSetAtom } from 'jotai';

export const ProjectCommentsTab = () => {
  const projectId = useAtomValue(projectIdState);
  const setModal = useSetAtom(modalState);

  const { data: projectCompanies, isFetched } = useProjectCompanies(projectId);
  const { data: comments } = useProjectComments();

  const { createComment } = useCreateProjectComment();
  const { updateComment, isUpdateInProgress } = useUpdateProjectComment();
  const { deleteComment: deleteProjectComment, isDeleteInProgress } =
    useDeleteProjectComment();

  if (!projectId) return null;

  const submitComment = async (comment: string, mentionedUsers: number[]) => {
    const data = await createComment({
      comment,
      projectId,
      mentionedUsers,
    });

    return data.id;
  };

  const openDeleteModal = async (commentId: string) => {
    setModal({
      state: 'confirmDelete',
      onConfirm: () => deleteComment(commentId),
    });
  };

  const deleteComment = async (commentId: string) =>
    await deleteProjectComment(commentId);

  const editComment = async (
    commentId: string,
    data: {
      comment: string;
      projectListingId?: string | null;
      mentionedUsers: number[];
    },
  ) => {
    const comment = await updateComment({
      commentId,
      comment: data.comment,
      ...(data.projectListingId !== undefined && {
        projectListingId: data.projectListingId,
      }),
      mentionedUsers: data.mentionedUsers,
    });
    return comment.id;
  };

  const setOptions: CustomAsyncSelectProps['setOptions'] = async search => {
    const response = await fetchProjectCompanies(projectId, {
      search,
    });

    return response.map(item => ({
      value: item.projectListingId,
      label: item.name,
      logo: item.logoUrl,
      domain: item.domain,
      organizationId: item.id,
      projectListingId: item.projectListingId,
    }));
  };

  return (
    <CommentsTab
      onSubmit={submitComment}
      comments={comments ?? []}
      onClickDelete={openDeleteModal}
      onEditSubmit={editComment}
      loading={isUpdateInProgress || isDeleteInProgress}
      dropdown={{
        setOptions,
        isDisabled: isFetched && !projectCompanies?.length,
      }}
    />
  );
};
